import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { Textarea } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { GraphNameService } from '../../../../../services/graph-name.service';
import { Graph, GraphTypeId } from '../../../../../types/graph.interface';
import { ApiErrorMessages } from '../../../../../utils/api-error-messages';
import { createUniqueNameValidator } from '../../../../../utils/validators';
import { CesDialogFooterComponent } from '../../../../ui/dialog-footer/dialog-footer.component';
import { GRAPH_TYPE_OPTIONS } from './graph-view.constants';

import { CesTooltipAutoDismissDirective } from '../../../../../directives/ces-tooltip-auto-dismiss.directive';
type GraphDialogMode = 'create' | 'edit' | 'duplicate';

interface GraphDialogData {
  readonly mode: GraphDialogMode;
  readonly graph?: Graph;
  readonly existingGraphNames?: string[];
}

interface GraphFormControls {
  name: FormControl<string>;
  type: FormControl<GraphTypeId | null>;
  description: FormControl<string>;
}

export interface CreateGraphDialogResult {
  name: string;
  type: GraphTypeId;
  description: string;
  shortName: string;
  id?: number;
}

@Component({
  selector: 'ces-create-graph-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CesTooltipAutoDismissDirective,
    ReactiveFormsModule,
    TranslocoModule,
    InputTextModule,
    Textarea,
    TooltipModule,
    CesDialogFooterComponent,
  ],
  templateUrl: './create-graph-dialog.component.html',
  styleUrl: './create-graph-dialog.component.scss',
})
export class CreateGraphDialogComponent {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly config = inject<DynamicDialogConfig<GraphDialogData>>(DynamicDialogConfig);
  private readonly graphNameService = inject(GraphNameService);
  private readonly translateService = inject(TranslocoService);

  private readonly mode = this.config.data?.mode ?? 'create';
  private readonly graph = this.config.data?.graph;
  private readonly existingGraphNames = this.config.data?.existingGraphNames ?? [];

  readonly graphTypes = GRAPH_TYPE_OPTIONS;

  readonly isTypeEditable = computed(() => this.mode === 'create');
  readonly form = this.createForm();

  private readonly nameStatus = toSignal(this.form.controls.name.statusChanges, {
    initialValue: this.form.controls.name.status,
  });
  private readonly submitTrigger = signal(0);

  readonly nameInvalid = computed(() => {
    this.nameStatus();
    this.submitTrigger();
    return this.form.controls.name.invalid && this.form.controls.name.dirty;
  });
  readonly nameNotUnique = computed(() => {
    this.nameStatus();
    this.submitTrigger();
    return this.form.controls.name.hasError('notUnique') && this.form.controls.name.dirty;
  });
  readonly typeInvalid = computed(() => {
    this.submitTrigger();
    return this.form.controls.type.invalid && this.form.controls.type.touched;
  });
  readonly nameNotUniqueMessage = computed(() => {
    const lang = this.translateService.getActiveLang() || 'en';
    return ApiErrorMessages[lang]?.['GRAPHIC_NAME_ALREADY_EXISTS'] ?? ApiErrorMessages['en']?.['GRAPHIC_NAME_ALREADY_EXISTS'] ?? '';
  });

  private createForm(): FormGroup<GraphFormControls> {
    const name = this.getInitialName();
    const type = this.getInitialType();
    const description = this.graph?.description ?? '';
    const isTypeEditable = this.mode === 'create';

    return new FormGroup<GraphFormControls>({
      name: new FormControl(name, {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.maxLength(100),
          createUniqueNameValidator(this.existingGraphNames, this.mode === 'edit' ? this.graph?.name : undefined),
        ],
      }),
      type: new FormControl<GraphTypeId | null>({ value: type, disabled: !isTypeEditable }, { validators: [Validators.required] }),
      description: new FormControl(description, { nonNullable: true }),
    });
  }

  private getInitialName(): string {
    if (this.mode === 'duplicate' && this.graph) {
      const copySuffix = this.translateService.translate('common.labels.copySuffix');
      return `${this.graph.name} - ${copySuffix}`;
    }
    return this.graph?.name ?? '';
  }

  private getInitialType(): GraphTypeId | null {
    return (this.graph?.type as GraphTypeId) ?? null;
  }

  isTypeSelected(type: GraphTypeId): boolean {
    const currentType = this.mode === 'create' ? this.form.controls.type.value : this.graph?.type;
    return currentType === type;
  }

  getCardClass(type: GraphTypeId): string {
    const selected = this.isTypeSelected(type);
    const baseClasses = selected ? 'border-(--p-blue-200) bg-(--p-blue-100)' : 'border-[var(--border)]';

    if (this.isTypeEditable()) {
      return `${baseClasses} cursor-pointer hover:border-(--p-blue-200) hover:bg-(--p-gray-100)`;
    }

    const opacityClass = selected ? '' : 'opacity-60';
    return `${baseClasses} ${opacityClass} cursor-not-allowed`;
  }

  selectType(type: GraphTypeId): void {
    if (!this.isTypeEditable()) return;
    this.form.controls.type.setValue(type);
    this.form.controls.type.markAsTouched();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      for (const control of Object.values(this.form.controls) as AbstractControl[]) {
        control.markAsDirty();
      }
      this.submitTrigger.update(v => v + 1);
      return;
    }

    const formValue = this.form.getRawValue();
    if (formValue.type === null) return;

    const shortName = this.graphNameService.generateShortName(formValue.name);
    const result: CreateGraphDialogResult = {
      name: formValue.name,
      type: formValue.type,
      description: formValue.description,
      shortName,
      ...(this.mode === 'edit' && this.graph ? { id: this.graph.id } : {}),
    };

    this.dialogRef.close(result);
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
